import { PermissionLiteral } from "@/gen_src/permissions";
import { Permission } from "@/gen_src/proto/chalk/auth/v1/permissions_pb";
import { compileOnlyAssertNever } from "@/utils/asserts";

export type ExtraPermissionLiteralGRPC =
  | "unspecified"
  | "insecure_unauthenticated"
  | "authenticated";

/**
 * Map the generated enum equivalent of the gRPC Permission enum to the
 * GQL-generated permission literal string value. This is nice if we want to display
 * the permission in the UI.
 *
 * TODO: codegen this file so that it stays in sync with the gRPC proto file.
 * Currently protoc-gen-es doesn't use the `option (chalk.utils.v1.encoding) = {`
 * portion of the Permission literal definition in the proto file, but maybe it could.
 */
export function permissionGrpcToLiteral(
  permission: Permission,
): PermissionLiteral | ExtraPermissionLiteralGRPC {
  switch (permission) {
    case Permission.UNSPECIFIED:
      return "unspecified";
    case Permission.INSECURE_UNAUTHENTICATED:
      return "insecure_unauthenticated";
    case Permission.AUTHENTICATED:
      return "authenticated";
    case Permission.QUERY_ONLINE:
      return "query.online";
    case Permission.QUERY_OFFLINE:
      return "query.offline";
    case Permission.QUERY_OFFLINE_READ:
      return "query.offline_read";
    case Permission.MONITORING_CREATE:
      return "monitoring.create";
    case Permission.MONITORING_READ:
      return "monitoring.read";
    case Permission.TEAM_ADD:
      return "team.add";
    case Permission.TEAM_DELETE:
      return "team.delete";
    case Permission.TEAM_LIST:
      return "team.list";
    case Permission.TEAM_ADMIN:
      return "team.admin";
    case Permission.DEPLOY_READ:
      return "deploy.read";
    case Permission.DEPLOY_CREATE:
      return "deploy.create";
    case Permission.DEPLOY_PREVIEW:
      return "deploy.preview";
    case Permission.DEPLOY_REDEPLOY:
      return "deploy.redeploy";
    case Permission.LOGS_LIST:
      return "logs.list";
    case Permission.CRON_READ:
      return "cron.read";
    case Permission.CRON_CREATE:
      return "cron.create";
    case Permission.SECRETS_WRITE:
      return "secrets.write";
    case Permission.SECRETS_DECRYPT:
      return "secrets.decrypt";
    case Permission.SECRETS_LIST:
      return "secrets.list";
    case Permission.TOKENS_WRITE:
      return "tokens.write";
    case Permission.TOKENS_LIST:
      return "tokens.list";
    case Permission.MIGRATE_READ:
      return "migrate.read";
    case Permission.MIGRATE_PLAN:
      return "migrate.plan";
    case Permission.MIGRATE_EXECUTE:
      return "migrate.execute";
    case Permission.PROJECT_CREATE:
      return "project.create";
    case Permission.CHALK_ADMIN:
      return "chalk.admin";
    case Permission.BILLING_READ:
      return "billing.read";
    case Permission.AUTH_SERVICE_MANAGER:
      return "auth_service.manager";
    case Permission.INFRASTRUCTURE_READ:
      return "infrastructure.read";
    case Permission.INFRASTRUCTURE_WRITE:
      return "infrastructure.write";
    case Permission.ENVIRONMENT_CREATE:
      return "environment.create";
    case Permission.INTERNAL_DATAPLANE_STATUS_UPDATE:
      return "internal.dataplane_status_update";
    case Permission.INTERNAL_WORKING_TOKEN_EXCHANGE:
      return "internal.working_token_exchange";
    case Permission.BILLING_WRITE:
      return "billing.write";
    case Permission.INTERNAL_RESOURCE_SHARE_VIEW:
      return "internal.resource_share_view";
    case Permission.INTERNAL_EXCHANGE_TOKEN_RENEW:
      return "internal.exchange_token_renew";
    case Permission.AGENTS_WRITE:
      return "agents.write";
    case Permission.AGENTS_READ:
      return "agents.read";
    case Permission.BILLING_PUBLISH_ANALYTICS:
      return "billing.publish_analytics";
    case Permission.NOTEBOOK_WRITE:
      return "notebook.write";
    default:
      compileOnlyAssertNever(permission);
      return "unspecified";
  }
}

export function permissionLiteralToGrpc(permission: PermissionLiteral): Permission {
  switch (permission) {
    case "query.online":
      return Permission.QUERY_ONLINE;
    case "query.offline":
      return Permission.QUERY_OFFLINE;
    case "query.offline_read":
      return Permission.QUERY_OFFLINE_READ;
    case "monitoring.create":
      return Permission.MONITORING_CREATE;
    case "monitoring.read":
      return Permission.MONITORING_READ;
    case "team.add":
      return Permission.TEAM_ADD;
    case "team.delete":
      return Permission.TEAM_DELETE;
    case "team.list":
      return Permission.TEAM_LIST;
    case "team.admin":
      return Permission.TEAM_ADMIN;
    case "deploy.read":
      return Permission.DEPLOY_READ;
    case "deploy.create":
      return Permission.DEPLOY_CREATE;
    case "deploy.preview":
      return Permission.DEPLOY_PREVIEW;
    case "deploy.redeploy":
      return Permission.DEPLOY_REDEPLOY;
    case "logs.list":
      return Permission.LOGS_LIST;
    case "cron.read":
      return Permission.CRON_READ;
    case "cron.create":
      return Permission.CRON_CREATE;
    case "secrets.write":
      return Permission.SECRETS_WRITE;
    case "secrets.decrypt":
      return Permission.SECRETS_DECRYPT;
    case "secrets.list":
      return Permission.SECRETS_LIST;
    case "tokens.write":
      return Permission.TOKENS_WRITE;
    case "tokens.list":
      return Permission.TOKENS_LIST;
    case "migrate.read":
      return Permission.MIGRATE_READ;
    case "migrate.plan":
      return Permission.MIGRATE_PLAN;
    case "migrate.execute":
      return Permission.MIGRATE_EXECUTE;
    case "project.create":
      return Permission.PROJECT_CREATE;
    case "chalk.admin":
      return Permission.CHALK_ADMIN;
    case "auth_service.manager":
      return Permission.AUTH_SERVICE_MANAGER;
    case "billing.read":
      return Permission.BILLING_READ;
    case "infrastructure.read":
      return Permission.INFRASTRUCTURE_READ;
    case "infrastructure.write":
      return Permission.INFRASTRUCTURE_WRITE;
    case "environment.create":
      return Permission.ENVIRONMENT_CREATE;
    case "internal.dataplane_status_update":
      return Permission.INTERNAL_DATAPLANE_STATUS_UPDATE;
    case "internal.working_token_exchange":
      return Permission.INTERNAL_WORKING_TOKEN_EXCHANGE;
    case "billing.write":
      return Permission.BILLING_WRITE;
    case "internal.resource_share_view":
      return Permission.INTERNAL_RESOURCE_SHARE_VIEW;
    case "internal.exchange_token_renew":
      return Permission.INTERNAL_EXCHANGE_TOKEN_RENEW;
    case "agents.write":
      return Permission.AGENTS_WRITE;
    case "agents.read":
      return Permission.AGENTS_READ;
    case "billing.publish_analytics":
      return Permission.BILLING_PUBLISH_ANALYTICS;
    case "notebook.write":
      return Permission.NOTEBOOK_WRITE;
    default:
      compileOnlyAssertNever(permission);
      return Permission.UNSPECIFIED;
  }
}
